import { i18n } from 'lib/i18n.js';

/**
 * This is a simple Vue.js mixin that checks for
 * a validate method on child components. If the method
 * exists it runs the method and ensures that the form
 * does not submit if the method returns a falsy value.
 */
export default {
  data() {
    return {
      validatableFields: [],
      totalItemsCount: 0,
      validItemsCount: 0,
    };
  },
  i18n,
  mounted() {
    this.initForm();
  },
  computed: {
    submitBtnClass() {
      if (this.validItemsCount === this.totalItemsCount) {
        return 'btn--primary';
      } else {
        return 'btn--outlined';
      }
    },
    submitBtnDisabled() {
      if (this.validItemsCount === this.totalItemsCount) {
        return false;
      } else {
        return true;
      }
    },
  },
  methods: {
    initForm() {
      this.validatableFields = this.$children.filter((c) => {
        return typeof c.validateInput !== 'undefined';
      });
      this.totalItemsCount = this.validatableFields.length;
      this.validItemsCount = this.getValidItemsCount();
    },
    getValidItemsCount() {
      return this.validatableFields
        .map((c) => {
          return c.validateInput({ skipError: true });
        })
        .filter((c) => c === true).length;
    },
    validateInput() {
      if (this.validateMethod) {
        const validation = this.validateMethod({
          value: this.inputValue,
          name: this.name,
        });

        this.isValid = validation.valid;
        this.errorMessage = validation.message;
      } else if (this.required) {
        if (!this.inputValue) {
          this.isValid = {
            valid: false,
            message: 'Field is required',
          };
        } else {
          this.isValid = {
            valid: true,
            message: false,
          };
        }
      } else {
        this.isValid = { valid: true, message: false };
      }
      return this.isValid;
    },
    formData(target = null) {
      const postData = {};
      if (!target) {
        target = this;
      }
      if (target) {
        this.$children.map((c) => {
          if (c.name && c.inputValue) {
            postData[c.name] = c.inputValue;
          }
          return c;
        });
      }
      return postData;
    },
    validateAndSubmitForm(e) {
      const isValid = this.$children
        .filter((c) => {
          return typeof c.validateInput !== 'undefined';
        })
        .map((c) => {
          const valid = c.validateInput();
          if (typeof valid === 'object') return valid.valid;
          this.highlightErrors(c, valid);
          return valid;
        })
        .every((res) => res);
      if (!isValid) {
        e.preventDefault();
      }
    },
    highlightErrors(element, isValid) {
      if (!isValid) {
        element.dirty = true;
        element.isValid = false;

        window.dataLayer.push({
          event: 'error_captured',
          error_message: element._props.error,
        });
      }
    },
    validateForm(e) {
      const isValid = this.$children
        .filter((c) => typeof c.validateInput !== 'undefined')
        .map((c) => {
          return c.validateInput().valid;
        })
        .every((res) => res);
      if (!isValid && e) {
        e.preventDefault();
      }
      return isValid;
    },
    valueChange() {
      this.validItemsCount = this.getValidItemsCount();
    },
    validateEmail(input) {
      const result = {
        valid: false,
        message: '',
      };
      // Disabling eslint in order to allow using control character in the new regex that's way faster.
      // eslint-disable-next-line
      const mailformat =
        /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
      result.message = this.$t('general.form_errors.required');
      if (input.value) {
        result.message = this.$t('general.form_errors.no_valid_email');
        if (input.value.match(mailformat)) {
          result.valid = true;
          result.message = false;
        }
      } else if (!input.required) {
        result.valid = true;
        result.message = false;
      }
      return result;
    },
    validateIsEqual(input) {
      const result = {
        valid: false,
        message: '',
      };
      const inputName = input.name.replace('confirm_', '');
      const equalInput = this.$el.querySelector(`input[name="${inputName}"]`);
      const firstValue = equalInput.value;
      const secondValue = input.value;

      if (firstValue === secondValue) {
        result.valid = true;
        result.message = false;
      }

      if (!secondValue) {
        result.valid = false;
        result.message = '';
      }

      return result;
    },
    validatePhone(input) {
      const result = {
        valid: false,
        message: '',
      };
      const phoneformat =
        /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
      result.message = this.$t('general.form_errors.required');
      if (input.value) {
        result.message = this.$t('general.form_errors.no_valid_phone');
        if (input.value.match(phoneformat)) {
          result.valid = true;
          result.message = false;
        }
      }
      return result;
    },
    validatePassword(input) {
      const result = {
        valid: false,
        message: '',
      };
      result.message = this.$t('general.form_errors.required');
      if (input.value) {
        result.message = this.$t('general.form_errors.password_length');
        if (input.value.length >= 5) {
          result.valid = true;
          result.message = false;
        }
      }
      return result;
    },
  },
};
